import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, Box, Typography } from '@mui/material';
import { Image } from '@mui/icons-material';

import { useChangeable, FullForm } from '../CommonComponents/Form';
import Loading from '../CommonComponents/Fetch/Loading';

import { cleanEntityMetadata } from '../../utils/metadata';
import {
    fetchSettableSniIndustries,
    selectSettableSniIndustries,
    selectFetchingSettableSniIndustries
} from '../../redux/actions/sni';
import {
    fetchSettableMetadataForTenant,
    selectFetchingSettableMetadataForTenant,
    selectSettableMetadataForTenant
} from '../../redux/actions/metadata';

import {
    createTenant,
    deleteTenant,
    getTenant,
    updateTenant
} from '../../api-admin/tenants';

import toast from '../../utils/toast';

import { SectionDivider } from '../CommonComponents';
import LogotypeHandler from './LogotypeComponents/LogotypeHandler';

import {
    BasicInfoBlock,
    BillingInfoBlock,
    ContactInfoBlock,
    EquipmentBlock,
    IndustryBlock,
    LicencesCertificatesBlock,
    SniBlock,
    SustainabilityBlock,
    VehicleBlock,
    VisitingAddressBlock,
    CoverageRoutesBlock
} from './FormComponents';
import TenantRecommendations from './TenantRecommendations';

const defaultTenant = {
    name: '',
    orgNumber: '',
    isCarrier: true,
    employees: null,
    revenue: null,
    numberOfTrucksOverride: null,
    website: '',

    email: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    lat: null,
    lng: null,

    billingEmail: '',
    billingMark: '',
    billingStreet: '',
    billingZip: '',
    billingCity: '',

    vehicleTypes: [],
    equipment: [],
    licencesCertificates: [],
    customLicencesCertificates: [],
    sustainability: [],
    sniIndustries: [],
    industries: [],
    fairtransportMembership: '',
    logoUrl: null as string | null,
    routes: [] as { id: number, name: string, points: { lat: number, lng: number }[] }[]
};

const BASIC_INFO_KEYS = [
    'name',
    'orgNumber',
    'isCarrier',
    'employees',
    'revenue',
    'numberOfTrucksOverride',
    'subscriptionEndDate',
    'activeSubscription'
] as const;

const BILLING_INFO_KEYS = [
    'billingEmail',
    'billingMark',
    'billingStreet',
    'billingZip',
    'billingCity'
] as const;

const CONTACT_INFO_KEYS = [
    'email',
    'phone',
    'website',
    'description'
] as const;

const VISITING_ADDRESS_KEYS = [
    'street',
    'zip',
    'city',
    'lat',
    'lng',
    'country'
] as const;

const SUSTAINABILITY_KEYS = [
    'sustainability',
    'fairtransportMembership'
] as const;

const LIC_CERT_KEYS = [
    'licencesCertificates',
    'customLicencesCertificates'
] as const;

const VEHICLE_KEYS = ['vehicleTypes'] as const;
const EQUIPMENT_KEYS = ['equipment'] as const;
const SNI_KEYS = ['sniIndustries'] as const;
const INDUSTRY_KEYS = ['industries'] as const;

const pickEntitySlice = <T extends Record<string, any>, K extends keyof T>(
    entity: T,
    keys: readonly K[]
): Pick<T, K> => Object.fromEntries(keys.map((key) => [key, entity[key]])) as Pick<T, K>;

const useEntitySlice = <T extends Record<string, any>, K extends keyof T>(
    entity: T,
    keys: readonly K[]
): Pick<T, K> => React.useMemo(() => pickEntitySlice(entity, keys), [entity, keys]);

const TenantForm: React.FC = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [entity, onChange] = useChangeable(defaultTenant);
    const [initialEntity, setInitialEntity] = React.useState(defaultTenant);

    const navigate = useNavigate();

    const settableSniIndustries = useSelector(selectSettableSniIndustries());
    const settableMetadata = useSelector(selectSettableMetadataForTenant());

    const isFetchingAllSniIndustries = useSelector(selectFetchingSettableSniIndustries());
    const isFetchingSettableMetadata = useSelector(selectFetchingSettableMetadataForTenant());

    const [
        customLicencesCertificates,
        setCustomLicencesCertificates
    ] = React.useState<Array<{ id: string; value: string }>>([]);

    const [sniIndustries, setSniIndustries] = React.useState<Array<{ sniCode: string; name: string }>>([]);

    const [industries, setIndustries] = React.useState<Array<{ id: string; value: string }>>([]);

    const basicInfoEntity = useEntitySlice(entity, BASIC_INFO_KEYS);
    const billingInfoEntity = useEntitySlice(entity, BILLING_INFO_KEYS);
    const contactInfoEntity = useEntitySlice(entity, CONTACT_INFO_KEYS);
    const visitingAddressEntity = useEntitySlice(entity, VISITING_ADDRESS_KEYS);
    const vehicleEntity = useEntitySlice(entity, VEHICLE_KEYS);
    const equipmentEntity = useEntitySlice(entity, EQUIPMENT_KEYS);
    const sustainabilityEntity = useEntitySlice(entity, SUSTAINABILITY_KEYS);
    const licCertEntity = useEntitySlice(entity, LIC_CERT_KEYS);
    const sniEntity = useEntitySlice(entity, SNI_KEYS);
    const industryEntity = useEntitySlice(entity, INDUSTRY_KEYS);

    const hasSettableMetadata = !isFetchingSettableMetadata
        && !isFetchingAllSniIndustries
        && Array.isArray(settableMetadata?.vehicleTypes)
        && Array.isArray(settableMetadata?.equipment)
        && Array.isArray(settableMetadata?.sustainability)
        && Array.isArray(settableMetadata?.licencesCertificates)
        && Array.isArray(settableSniIndustries);

    const onSubmit = React.useCallback(async () => {
        try {
            const cleanedEntity = cleanEntityMetadata(entity);
            if (id) {
                await updateTenant(id, cleanedEntity);
                toast('Företag uppdaterat');
                navigate(-1);
            } else {
                const createdUser = await createTenant(cleanedEntity);
                toast('Företag skapat');
                navigate(`/tenants/${createdUser.id}`, { replace: true });
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} företaget, försök igen`);
        }
    }, [id, entity, navigate]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteTenant(id);
                toast('Företaget borttaget');
                navigate(-2);
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort företaget, försök igen');
        }
    }, [id, navigate]);

    const refreshTenant = React.useCallback(async () => {
        if (!id) return;
        const tenant = await getTenant(id);
        onChange(tenant);
    }, [id, onChange]);

    React.useEffect(() => {
        dispatch(fetchSettableSniIndustries());
        dispatch(fetchSettableMetadataForTenant());
    }, [dispatch]);

    React.useEffect(() => {
        (async () => {
            if (id) {
                const t = await getTenant(id);
                setInitialEntity(t);
                onChange(t);
            }
        })();
    }, [id, onChange]);

    React.useEffect(() => {
        const initialSnis = initialEntity.sniIndustries || [];
        const mappedSnis = initialSnis.map((sni: string) => {
            const firstSpaceIndex = sni.indexOf(' ');
            const sniCode = firstSpaceIndex > -1 ? sni.substring(0, firstSpaceIndex) : sni;
            const name = firstSpaceIndex > -1 ? sni.substring(firstSpaceIndex + 1) : '';
            return { sniCode, name };
        });
        setSniIndustries(mappedSnis);

        const mappedLicCert = (initialEntity.customLicencesCertificates || [])
            .map((cert, idx) => ({ id: `init-cert-${idx}`, value: cert }));
        setCustomLicencesCertificates(mappedLicCert);

        const mappedIndustries = (initialEntity.industries || [])
            .map((ind, idx) => ({ id: `init-ind-${idx}`, value: ind }));
        setIndustries(mappedIndustries);
    }, [initialEntity]);

    return (
        isFetchingSettableMetadata
            ? (Loading({ name: 'företaget' }))
            : (
                <FullForm
                    title={`${id ? 'Redigera' : 'Skapa'} företag`}
                    entity={entity}
                    onSubmit={onSubmit}
                    onDelete={id ? onDelete : undefined}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={4}>
                            <SectionDivider
                                label="Logotyp"
                                icon={<Image fontSize="small" />}
                                marginTop={2}
                                marginBottom={0}
                            />
                            {id ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '100%',
                                    py: 4,
                                }}
                                >
                                    <LogotypeHandler
                                        tenantId={id}
                                        url={entity.logoUrl}
                                        name={entity.name}
                                        size={100}
                                        showButton
                                        onUploaded={refreshTenant}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Spara företaget först för att kunna ladda upp en logotyp.
                                </Typography>
                            )}
                        </Grid>
                        <BasicInfoBlock entity={basicInfoEntity} onChange={onChange} />
                        <BillingInfoBlock entity={billingInfoEntity} onChange={onChange} />
                        <VisitingAddressBlock entity={visitingAddressEntity} onChange={onChange} />
                        <ContactInfoBlock entity={contactInfoEntity} onChange={onChange} />
                        {id && (
                            <TenantRecommendations tenantId={Number(id)} editable />
                        )}

                        {hasSettableMetadata && (
                            <>
                                {entity.isCarrier && (
                                    <>
                                        <VehicleBlock
                                            settables={settableMetadata.vehicleTypes}
                                            entity={vehicleEntity}
                                            onChange={onChange}
                                        />
                                        <EquipmentBlock
                                            settables={settableMetadata.equipment}
                                            entity={equipmentEntity}
                                            onChange={onChange}
                                        />
                                        <SustainabilityBlock
                                            settables={settableMetadata.sustainability}
                                            entity={sustainabilityEntity}
                                            onChange={onChange}
                                        />
                                        <LicencesCertificatesBlock
                                            settables={settableMetadata.licencesCertificates}
                                            entity={licCertEntity}
                                            onChange={onChange}
                                            customLicCerts={customLicencesCertificates}
                                            setterCustomLicCerts={setCustomLicencesCertificates}
                                        />
                                        <CoverageRoutesBlock
                                            entity={entity}
                                            onChange={onChange}
                                        />
                                    </>
                                )}
                                <SniBlock
                                    entity={sniEntity}
                                    onChange={onChange}
                                    settables={settableSniIndustries}
                                    isFetching={isFetchingAllSniIndustries}
                                    snis={sniIndustries}
                                    setterSnis={setSniIndustries}
                                />
                                <IndustryBlock
                                    entity={industryEntity}
                                    onChange={onChange}
                                    industries={industries}
                                    setterIndustries={setIndustries}
                                />
                            </>
                        )}
                    </Grid>
                </FullForm>
            )
    );
};

export default TenantForm;
